import {
  InlineMessage,
  Modal,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import { makeStyles } from '@material-ui/core';
import React, { VFC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { updateApplicantTableStatuses } from 'containers/ApplicantTableV2/actions';
import { openMoveApplicantPopup } from 'containers/GlobalPopup/actions';
import { fetchApplicantData } from 'hooks/useApplicantDetail';
import { dispatchApplicantsInvalidated } from 'utils/appEvents';

import { messages } from './messages';
import { ScorecardForm } from './ScorecardForm';

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 20,
    fontWeight: theme.typography.fontWeightBold as number,
  },
}));

export interface ScoreApplicantModalProps {
  applicantId: string;
  scorecardTemplateId: string;
  scorecardName: string;
  // A completed status label's advance waits on this scorecard: the modal
  // says so, and a submission opens the advance confirmation.
  advanceOnSubmit?: boolean;
  handleClose: () => void;
}

export const ScoreApplicantModal: VFC<ScoreApplicantModalProps> = ({
  applicantId,
  scorecardTemplateId,
  scorecardName,
  advanceOnSubmit = false,
  handleClose,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // The submitted scorecard changes the applicant's status labels; refetch
  // the detail so the label flips without a page reload.
  const handleSubmitted = () => {
    handleClose();
    // The move dialog re-checks the requirement on confirm, so a submission
    // that still leaves it unmet ends in the Scorecard required modal.
    if (advanceOnSubmit) {
      dispatch(
        openMoveApplicantPopup({ applicantId, transitionType: 'advance' }),
      );
    }
    dispatchApplicantsInvalidated([applicantId]);
    void fetchApplicantData(applicantId)
      .then(response => {
        dispatch(
          updateApplicantTableStatuses(
            applicantId,
            response.data.applicant.status,
          ),
        );
      })
      .catch(() => undefined);
  };

  return (
    <Modal
      data-testid="score-applicant-modal"
      ariaLabelledBy="score-applicant-modal-header"
      open
      onClose={handleClose}
      fullScreenOnMobile
      maxWidth={860}
    >
      <ModalHeader
        onClose={handleClose}
        ariaLabelledBy="score-applicant-modal-header"
        showIcon={false}
      >
        <span className={classes.title}>{scorecardName}</span>
      </ModalHeader>
      <ScorecardForm
        variant="modal"
        applicantId={applicantId}
        scorecardTemplateId={scorecardTemplateId}
        onCancel={handleClose}
        onSubmitted={handleSubmitted}
        notice={
          advanceOnSubmit ? (
            <InlineMessage
              type="orange"
              message={
                <FormattedMessage {...messages.advanceAfterSubmitNotice} />
              }
            />
          ) : undefined
        }
      />
    </Modal>
  );
};
